import { echo } from '@decisions/server/DecisionsFramework/ServiceLayer/Services/ClientEvents/ClientEventsService';
import { DataPair } from '@decisions/server/DecisionsFramework/ServiceLayer/Services/ContextData/DataPair';
import {
  HttpTransportType,
  HubConnection,
  HubConnectionBuilder,
  HubConnectionState,
  IHttpConnectionOptions,
  LogLevel,
} from '@microsoft/signalr';
import { createEvent } from 'utils/DomUtils';
import { generateGuid } from 'utils/Guid';
import { QueueUtil } from './QueueUtils';
import { renderServiceDisconnectedPopup } from '../components/Portal/ServiceDisconnectedPopup';
import { getRegisteredListenerIds, setConnectionReady } from './ClientEventUtils';
import { getIsMobile } from 'config/PortalConfiguration';

const EVENTS_LISTENER_NAME = 'publishEvents';

export class EventsHub {
  connection: HubConnection;
  connectionPromise: Promise<void>;
  transportType = HttpTransportType.WebSockets;
  skipNegotiation = true;
  queue = new QueueUtil();
  timer: number | undefined = undefined;
  shouldRetry: boolean = true;

  get jsClientEventSessionId() {
    if (!jsClientEventSessionId) {
      jsClientEventSessionId = generateGuid();
    }

    return jsClientEventSessionId;
  }

  constructor() {
    if (IS_TEST) {
      // we don't want to build connection, etc. in a JEST test context
      this.connection = require('./__mocks__/SingalRMocks').getMockHubConnection();
      this.connectionPromise = new Promise(resolve => {
        // don't need to do anything, yet, as we are not actively testing this, just keeping it from breaking other things
        resolve();
      });
      return;
    }

    this.connection = this.buildConnection();
    const initializeConnection = async (resolve: any) => {
      const timeout = setTimeout(async () => {
        if (Decisions?.ClientEventsHandler?.SessionRegistrationComplete === true) {
          await this.startConnection();
          clearTimeout(timeout);
          resolve();
        } else {
          initializeConnection(resolve);
        }
      }, 100);
    };
    this.connectionPromise = new Promise(resolve => {
      initializeConnection(resolve);
    });
  }

  buildConnection() {
    // build the connection object
    return new HubConnectionBuilder()
      .withUrl(`${virtualPath}/events?clientEventSessionId=${this.jsClientEventSessionId}`, {
        transport: this.transportType,
        skipNegotiation: this.skipNegotiation,
        logger: LogLevel.Trace,
      } as IHttpConnectionOptions)
      .build();
  }

  updateClientEventSessionId = (clientEventSessionId: string) => {
    jsClientEventSessionId = clientEventSessionId;
  };

  async startConnection() {
    // start the connection
    try {
      // attach event before starting the connection.
      this.connection.on('updateClientEventSessionId', this.updateClientEventSessionId);
      await this.connection.start();
      this.onConnectionSuccess();
    } catch (err) {
      console.warn('Unable to start the events connection.', err);
      // if transport type is not long polling, try to connect with long polling
      if (this.shouldRetry) {
        this.shouldRetry = false;
        const { getPortalSetup } = await import('./PortalSetup');
        const portalSetup = getPortalSetup();
        if (
          portalSetup.TurnOnClustering === false ||
          portalSetup.FeatureFlags.UseClusteringWithoutNodeAffinity === false
        ) {
          console.log('Switching the connection to long polling.');
          this.transportType = HttpTransportType.LongPolling;
          this.skipNegotiation = false;
        }
        this.connection = this.buildConnection();
        await this.startConnection();
      } else {
        // should throw error to catch it if called from connection close handler.
        throw err;
      }
    }
  }

  onConnectionSuccess() {
    this.connection.off(EVENTS_LISTENER_NAME);
    this.connection.on(EVENTS_LISTENER_NAME, this.publishEvents);
    this.afterConnectionSuccess();
    this.shouldRetry = true;
    this.connection.onclose(async (error?: Error) => {
      // check if connection state is disconnected
      if (this.connection.state === HubConnectionState.Disconnected) {
        console.warn(error);
        // connection closed
        try {
          // start the connection again.
          await this.startConnection();
        } catch (err) {
          // error starting connection, call echo to confirm
          try {
            await echo();
            await this.startConnection();
          } catch (err) {
            // Error while calling Echo, service is down, show disconnected popup
            renderServiceDisconnectedPopup();
          }
        }
      }
    });
  }

  /*
    Directly invokes the register event, does not add it in the queue.
  */
  async invokeRegisterEvent(eventId: string) {
    await this.connectionPromise;
    await this.connection.invoke('RegisterEvent', eventId);
  }

  /** Registers the event on the server. */
  async registerEvent(eventId: string) {
    await this.connectionPromise;
    this.queue.enqueue({ callback: () => this.connection.invoke('RegisterEvent', eventId) });
  }

  /** Unregister the event on the server. */
  async unRegisterEvent(eventId: string) {
    await this.connectionPromise;
    this.queue.enqueue({ callback: () => this.connection.invoke('UnRegisterEvent', eventId) });
  }

  async registerEvents(eventIds: string[]) {
    await this.connectionPromise;
    this.queue.enqueue({ callback: () => this.connection.invoke('RegisterEvents', eventIds) });
  }

  /** Updates the user folders on the server. */
  async updateUserFolders() {
    await this.connectionPromise;
    let visibleFolders: string[];
    if (getIsMobile()) {
      const { getMobileFolderIdFromUrl } = await import('domains/mobile/MobilePortal/utils/MobileRoutingUtils');
      const mobileFolderId = getMobileFolderIdFromUrl();
      visibleFolders = mobileFolderId ? [mobileFolderId] : [];
    } else {
      const { getVisibleFolders } = await import('components/Router/RouterUtils');
      visibleFolders = getVisibleFolders();
    }
    visibleFolders?.length &&
      this.queue.enqueue({ callback: () => this.connection.invoke('UpdateUserFolders', visibleFolders) });
  }

  afterConnectionSuccess() {
    // clear the timer
    if (this.timer) {
      clearTimeout(this.timer);
    }

    this.updateUserFolders();
    this.registerEvents(getPubSub().GetRegisterMessegeeNames());
    setConnectionReady();

    // start the 15min timer
    this.timer = window.setTimeout(() => {
      this.afterConnectionSuccess();
    }, 15 * 60 * 1000);
  }

  /** Receives the event from the server and publishes the event here. */
  publishEvents = (events: DataPair[]) => {
    events.forEach(event => {
      getPubSub().publishSync(event.Name, event.OutputValue);
      window.dispatchEvent(createEvent(event.Name, event.OutputValue));
    });
  };
}

// add jsClientEventSessionId on window object if not present
!(window as any).jsClientEventSessionId && ((window as any).jsClientEventSessionId = undefined);
// create the object of EventsHub and export it
const eventsHub = new EventsHub();

(function(ps) {
  const originalSubscribe = ps.subscribe;
  ps.subscribe = (message: string, func: any) => {
    if (message == null) {
      throw new Error('Attempting to subscribe with a null message name');
    }
    eventsHub.registerEvent(message);
    return originalSubscribe(message, func);
  };

  ps.GetRegisterMessegeeNames = function() {
    // these might have duplicates for now, but will be merged server side
    return [...ps.getMessageNames(), ...getRegisteredListenerIds()];
  };
})(getPubSub());

export { eventsHub };

export function getPubSub(): typeof PubSub {
  if (IS_TEST || DEV_SERVER || process.env.STORYBOOK) {
    return require('./__mocks__/PubSubMock').pubSubMock;
  }
  return PubSub;
}
